import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Project, ProjectStatus } from "~/api/governanceTypes";
import { useCreateProject, useGroups, useUpdateProject } from "~/api/queries";
import { Select, TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";

const EDITABLE_STATUSES: ProjectStatus[] = ["active", "onHold", "completed", "cancelled"];

/** Literal `t()` calls, one per case, so the extractor can see every label at the call site —
 * the same discipline `statusPresentation` uses for run/flow statuses. */
export function projectStatusLabel(status: ProjectStatus, t: (key: string) => string): string {
  switch (status) {
    case "onHold":
      return t("On hold");
    case "completed":
      return t("Completed");
    case "cancelled":
      return t("Cancelled");
    case "archived":
      return t("Archived");
    default:
      return t("Active");
  }
}

interface ProjectFormState {
  name: string;
  description: string;
  groupId: string;
  icon: string;
  color: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
}

function initialState(project?: Project): ProjectFormState {
  return {
    name: project?.name ?? "",
    description: project?.description ?? "",
    groupId: project?.groupId ?? "",
    icon: project?.icon ?? "",
    color: project?.color ?? "",
    status: project?.status ?? "active",
    startDate: project?.startDate ?? "",
    endDate: project?.endDate ?? "",
  };
}

/**
 * Create and edit, one form, two modes. Name is the only required field — the
 * `new-project-page` philosophy: intent to a working project in under 30 seconds.
 */
export function ProjectDialog({ project, onClose }: { project?: Project; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: groups } = useGroups();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const [form, setForm] = useState<ProjectFormState>(() => initialState(project));
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Autofocus in an effect — Biome forbids the `autoFocus` attribute.
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const isEdit = Boolean(project);
  const busy = createProject.isPending || updateProject.isPending;

  const submit = () => {
    const name = form.name.trim();
    if (!name) {
      return;
    }
    const shared = {
      name,
      description: form.description.trim() || undefined,
      groupId: form.groupId || null,
      icon: form.icon.trim() || null,
      color: form.color.trim() || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };
    if (isEdit && project) {
      updateProject.mutate(
        { id: project.id, etag: project.etag, ...shared, status: form.status },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Project saved"));
          },
        },
      );
    } else {
      createProject.mutate(shared, {
        onSuccess: (created) => {
          onClose();
          toast.success(t("Created “{{title}}”", { title: created.name }));
        },
      });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Edit project") : t("New project")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!form.name.trim() || busy} onClick={submit}>
            {busy ? (isEdit ? t("Saving…") : t("Creating…")) : isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          ref={nameRef}
          label={t("Name")}
          fullWidth
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <TextArea
          label={t("Description")}
          value={form.description}
          rows={3}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />
        <Select
          label={t("Team")}
          fullWidth
          value={form.groupId}
          onChange={(event) => setForm({ ...form, groupId: event.target.value })}
        >
          <option value="">{t("No team")}</option>
          {(groups ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>

        {isEdit ? (
          <Select
            label={t("Status")}
            fullWidth
            value={form.status}
            onChange={(event) => setForm({ ...form, status: event.target.value as ProjectStatus })}
          >
            {EDITABLE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {projectStatusLabel(status, t)}
              </option>
            ))}
          </Select>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Input
            type="date"
            label={t("Start date")}
            value={form.startDate}
            onChange={(event) => setForm({ ...form, startDate: event.target.value })}
          />
          <Input
            type="date"
            label={t("End date")}
            value={form.endDate}
            onChange={(event) => setForm({ ...form, endDate: event.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("Icon (emoji)")}
            value={form.icon}
            maxLength={4}
            onChange={(event) => setForm({ ...form, icon: event.target.value })}
          />
          <Input
            label={t("Colour")}
            placeholder="#4C6EF5"
            value={form.color}
            onChange={(event) => setForm({ ...form, color: event.target.value })}
          />
        </div>
      </div>
    </Dialog>
  );
}
